import {
  HDRecordingStatus,
  MediaRoomState,
  ProgressiveCloudProxyStatus,
  SDRecordingStatus,
  SignalMetadata,
  TurnServerFallbackStatus
} from './types';

import type {
  IAgoraRTCRemoteUser,
  ConnectionState,
  NetworkQuality,
  ConnectionDisconnectedReason,
  ILocalTrack,
  UID,
  AIDenoiserProcessorLevel
} from '@/services/agora.types';

import { appMode } from '@/assets/utilfunctions';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { MediaRoom, RtcDevices, AgoraToken } from '@/services/liveMedia.service';
import { getValueFromLocalstore, saveValueToLocalstore } from '@/services/localStorage.service';
import { LocalTrack } from '@/services/stream.service';

const localStorageStringForMediaType = {
  camera: `${appMode}Camera`,
  microphone: `${appMode}Microphone`,
  speaker: `${appMode}Speaker`
};
const mutations = {
  addMediaRoom(state: MediaRoomState, room: MediaRoom) {
    const client = room.client;
    const screenShareClient = room.screenShareClient;
    //@ts-ignore
    delete room.client;
    //@ts-ignore
    delete room.screenShareClient;
    const existingRoom = state.mediaRooms[room.channelName];
    if (existingRoom) {
      if (client && existingRoom.client) {
        //@ts-ignore
        delete existingRoom.client;
      }

      if (screenShareClient && existingRoom.screenShareClient) {
        //@ts-ignore
        delete existingRoom.screenShareClient;
      }
      state.mediaRooms[room.channelName] = { ...room, ...existingRoom };
    } else {
      Object.assign(state.mediaRooms, { [`${room.channelName}`]: room });
    }

    state.mediaRooms[room.channelName].client = client;
    state.mediaRooms[room.channelName].screenShareClient = screenShareClient;
    room.client = client;
    room.screenShareClient = screenShareClient;
  },
  setBulkAgoraTokens(state: MediaRoomState, tokens: AgoraToken[]) {
    state.agoraTokens = tokens;
  },
  setLocalTrack(state: MediaRoomState, track: LocalTrack) {
    state.localTracks = [...state.localTracks, track];
  },
  setScreenTrack(state: MediaRoomState, track: LocalTrack) {
    state.screenTracks.push(track);
  },
  setRtcDevices(state: MediaRoomState, devices: RtcDevices) {
    const rtcDeviceMap: RtcDevices = { ...state.rtcDevices, ...devices };
    const sanitizer = (devices: MediaDeviceInfo[]) => {
      return devices.filter(device => {
        return device?.label && device?.deviceId;
      });
    };
    state.rtcDevices = {
      cameras: sanitizer(rtcDeviceMap.cameras),
      microphones: sanitizer(rtcDeviceMap.microphones),
      speakers: sanitizer(rtcDeviceMap.speakers)
    };
  },
  setRtcDeviceAccessError(
    state: MediaRoomState,
    { errorDevice, errorFlag }: { errorDevice: 'camera' | 'microphone' | 'speaker'; errorFlag: Boolean }
  ) {
    state.rtcDeviceAccessError[errorDevice] = errorFlag;
  },
  setRtcDeviceBrowserPermission(
    state: MediaRoomState,
    {
      deviceType,
      permission
    }: {
      deviceType: 'camera' | 'microphone' | 'speaker';
      permission: Boolean;
    }
  ) {
    state.rtcDeviceBrowserPermission[deviceType] = permission;
  },
  setRtcDeviceSystemPermission(
    state: MediaRoomState,
    {
      deviceType,
      permission
    }: {
      deviceType: 'camera' | 'microphone' | 'speaker';
      permission: Boolean;
    }
  ) {
    state.rtcDeviceSystemPermission[deviceType] = permission;
  },
  setCurrentDevice(
    state: MediaRoomState,
    {
      device,
      deviceType
    }: {
      device: MediaDeviceInfo;
      deviceType: 'camera' | 'microphone' | 'speaker';
    }
  ) {
    let stringifiedDevice = undefined;
    if (device) {
      //@ts-ignore
      stringifiedDevice = JSON.stringify(device);
      window.localStorage.setItem(localStorageStringForMediaType[deviceType], JSON.stringify(device));
    }
    state.currentDevices[deviceType] = stringifiedDevice && JSON.parse(stringifiedDevice);
  },
  setStageControl(state: MediaRoomState, value: Object) {
    state.stageControls = {
      ...state.stageControls,
      ...value
    };
    saveValueToLocalstore(`${window.eventid}-stageControls`, state.stageControls);
  },
  initStageControls(state: MediaRoomState, eventID: string) {
    const stageControls = getValueFromLocalstore(`${eventID}-stageControls`, null);
    if (stageControls) {
      state.stageControls = stageControls;
    }
  },
  updateLocalTrack(
    state: MediaRoomState,
    {
      channelName,
      mediaTrackType,
      updateObj
    }: {
      channelName: MediaRoom['channelName'];
      mediaTrackType: ILocalTrack['trackMediaType'];
      updateObj: Partial<ILocalTrack>;
    }
  ) {
    state.localTracks = state.localTracks.map(localTrack => {
      if (localTrack.channelName == channelName && localTrack.mediaTrackType == mediaTrackType) {
        return { ...localTrack, ...updateObj };
      }

      return localTrack;
    });
  },
  updateScreenTrack(
    state: MediaRoomState,
    {
      channelName,
      mediaTrackType,
      updateObj
    }: {
      channelName: MediaRoom['channelName'];
      mediaTrackType: ILocalTrack['trackMediaType'];
      updateObj: Partial<ILocalTrack>;
    }
  ) {
    state.screenTracks = state.screenTracks.map(screenTrack => {
      if (screenTrack.channelName == channelName && screenTrack.mediaTrackType == mediaTrackType) {
        return { ...screenTrack, ...updateObj };
      }

      return screenTrack;
    });
  },
  removeLocalTrack(
    state: MediaRoomState,
    {
      channelName,
      mediaTrackType
    }: {
      channelName: MediaRoom['channelName'];
      mediaTrackType: ILocalTrack['trackMediaType'];
    }
  ) {
    state.localTracks = state.localTracks.filter(
      el => !(el.channelName == channelName && el.mediaTrackType == mediaTrackType)
    );
  },
  removeMediaRoom(state: MediaRoomState, channelName: string) {
    // const mediaRooms = { ...state.mediaRooms };
    // @ts-ignore
    state.mediaRooms[channelName] = undefined;
    delete state.mediaRooms[channelName];
    // state.mediaRooms = { ...mediaRooms };
  },
  addOrUpdateUserTrack(
    state: MediaRoomState,
    {
      mediaType,
      channelName,
      user
    }: {
      mediaType: 'audio' | 'video';
      channelName: string;
      user: IAgoraRTCRemoteUser;
    }
  ) {
    const userId = user.uid;
    const trackObject: any = {};
    if (mediaType === 'audio') {
      trackObject.audioTrack = user.audioTrack;
    } else if (mediaType === 'video') {
      trackObject.videoTrack = user.videoTrack;
    }

    const existingUserStream: any = state.mediaRooms[channelName]?.remoteStreams[userId] || { capacityVerified: false };
    if (existingUserStream) {
      Object.assign(state.mediaRooms[channelName].remoteStreams, {
        [userId]: { ...existingUserStream, ...trackObject }
      });
    } else if (state.mediaRooms[channelName]?.remoteStreams) {
      Object.assign(state.mediaRooms[channelName].remoteStreams, {
        [userId]: {
          ...existingUserStream,
          ...trackObject
        }
      });
    } else if (state.mediaRooms[channelName]) {
      Object.assign(state.mediaRooms[channelName], {
        remoteStreams: {
          [userId]: {
            ...existingUserStream,
            ...trackObject
          }
        }
      });
    } else {
      Object.assign(state.mediaRooms, {
        [channelName]: {
          remoteStreams: {
            [userId]: {
              ...existingUserStream,
              ...trackObject
            }
          }
        }
      });
    }
  },
  addOrUpdateUserData(
    state: MediaRoomState,
    { channelName, userData, userId }: { channelName: string; userData: any; userId: string }
  ) {
    if (!state.mediaRooms?.[channelName]?.remoteStreams?.[userId]) {
      return;
    }

    Object.assign(state.mediaRooms[channelName].remoteStreams[userId], { user: userData });
  },
  capacityVerified(state: MediaRoomState, { channelName, user }: { channelName: string; user: IAgoraRTCRemoteUser }) {
    if (!state.mediaRooms?.[channelName]?.remoteStreams?.[user.uid]) {
      return;
    }

    Object.assign(state.mediaRooms[channelName].remoteStreams[user.uid], { capacityVerified: true });
  },
  removeRemoteUser(
    state: MediaRoomState,
    { channelName, userId }: { channelName: string; userData: any; userId: string }
  ) {
    const remoteStreams = state.mediaRooms[channelName].remoteStreams;
    // @ts-ignore
    remoteStreams[userId] = undefined;
    delete remoteStreams[userId];
  },

  setMediaChannelStatus(
    state: MediaRoomState,
    {
      channelName,
      status,
      connectionDisconnectedReason
    }: {
      channelName: string;
      status: ConnectionState;
      connectionDisconnectedReason?: ConnectionDisconnectedReason;
    }
  ) {
    if (status === 'CONNECTED') {
      state.didEstablishConnection = true;
    }
    if (state.mediaRooms[channelName]) {
      Object.assign(state.mediaRooms[channelName], { status: status });
      Object.assign(state.mediaRooms[channelName], { connectionDisconnectedReason: connectionDisconnectedReason });
    } else {
      Object.assign(state.mediaRooms, {
        [`${channelName}`]: {
          status,
          connectionDisconnectedReason
        }
      });
    }
  },
  setMediaChannelDesiredStatus(
    state: MediaRoomState,
    {
      channelName,
      desiredStatus
    }: {
      channelName: string;
      desiredStatus: MediaRoom['desiredStatus'];
    }
  ) {
    if (state.mediaRooms[channelName]) {
      Object.assign(state.mediaRooms[channelName], { desiredStatus: desiredStatus });
    } else {
      Object.assign(state.mediaRooms, { [`${channelName}`]: { desiredStatus: desiredStatus } });
    }
  },
  setMediaChannelScreenStatus(
    state: MediaRoomState,
    {
      channelName,
      status,
      connectionDisconnectedReason
    }: {
      channelName: string;
      status: ConnectionState;
      connectionDisconnectedReason?: ConnectionDisconnectedReason;
    }
  ) {
    if (state.mediaRooms[channelName]) {
      Object.assign(state.mediaRooms[channelName], { screenStatus: status });
      Object.assign(state.mediaRooms[channelName], {
        screenConnectionDisconnectedReason: connectionDisconnectedReason
      });
    } else {
      Object.assign(state.mediaRooms, {
        [`${channelName}`]: {
          screenStatus: status,
          screenConnectionDisconnectedReason: connectionDisconnectedReason
        }
      });
    }
  },
  setMediaChannelMuteStatus(
    state: MediaRoomState,
    {
      channelName,
      isMuted
    }: {
      channelName: string;
      isMuted: boolean;
    }
  ) {
    if (state.mediaRooms[channelName]) {
      Object.assign(state.mediaRooms[channelName], { isMuted: isMuted });
    } else {
      Object.assign(state.mediaRooms, { [`${channelName}`]: { isMuted: isMuted } });
    }
  },
  setSignalMetadata(state: MediaRoomState, { networkQuality }: { networkQuality: NetworkQuality }) {
    state.signalMetadata.networkQuality = networkQuality;
    const averageNetworkQuality =
      ((networkQuality.uplinkNetworkQuality || 2) + (networkQuality.downlinkNetworkQuality || 2)) / 2;
    state.signalMetadata.strength = 6 - Math.round(averageNetworkQuality);
  },
  setActiveSpeakerId(
    state: MediaRoomState,
    {
      channelName,
      activeSpeakerId
    }: {
      channelName: string;
      activeSpeakerId: UID;
    }
  ) {
    if (state.mediaRooms[channelName]) {
      Object.assign(state.mediaRooms[channelName], { activeSpeakerId: activeSpeakerId });
    } else {
      Object.assign(state.mediaRooms, { [`${channelName}`]: { activeSpeakerId: activeSpeakerId } });
    }
  },
  removeScreenTracks(state: MediaRoomState) {
    state.screenTracks = [];
  },
  setUserRoleInMediaRoom(
    state: MediaRoomState,
    {
      channelName,
      userRole
    }: {
      channelName: string;
      userRole: MediaRoom['userRole'];
    }
  ) {
    if (state.mediaRooms[channelName]) {
      Object.assign(state.mediaRooms[channelName], { userRole: userRole });
    } else {
      Object.assign(state.mediaRooms, { [`${channelName}`]: { userRole } });
    }
  },
  setNetworkErrorIndex(state: MediaRoomState, value: Number | undefined) {
    state.networkErrorIndex = value;
  },
  setCurrentBackgroundFilter(state: MediaRoomState, value: string) {
    state.currentBackgroundFilter = value;
  },
  setUserBackgroundFilterPrefrence(state: MediaRoomState, payload: { image?: Boolean; blur?: Boolean }) {
    const preferences = {
      ...state.userBackgroundFilterPreference,
      ...payload
    };
    state.userBackgroundFilterPreference = preferences;
  },
  setRemoteSignalMetadata(
    state: MediaRoomState,
    payload: {
      remoteNetworkQuality: { [uid: string]: NetworkQuality };
      channelName: string;
    }
  ) {
    if (!payload.remoteNetworkQuality) {
      return;
    }

    const remoteSignalMetadata: {
      [uid: string]: SignalMetadata;
    } = Object.keys(payload.remoteNetworkQuality).reduce((acc, uid) => {
      const worseNetworkQuality = Math.max(
        payload.remoteNetworkQuality[uid].downlinkNetworkQuality,
        payload.remoteNetworkQuality[uid].uplinkNetworkQuality
      );

      return {
        ...acc,
        [uid]: {
          networkQuality: payload.remoteNetworkQuality[uid],
          strength: 6 - worseNetworkQuality
        }
      };
    }, {});

    state.remoteSignalMetadata = remoteSignalMetadata;
  },
  toggleMirrorSelf(state: MediaRoomState) {
    state.mirrorSelf = !state.mirrorSelf;
    saveValueToLocalstore(LOCAL_STORAGE_KEYS.isUserVideoMirrored, state.mirrorSelf);
  },
  updateMuteNotificationState(state: MediaRoomState, status: Boolean) {
    state.showMuteNotifications = status;
  },
  setNoiseCancellation(state: MediaRoomState, status: Boolean) {
    state.noiseCancellationEnabled = status;
  },
  setNoiseCancellationLevel(state: MediaRoomState, level: AIDenoiserProcessorLevel) {
    state.selectedNoiseSuppressionLevel = level;
  },
  setNoiseCancellationCompatible(state: MediaRoomState, status: Boolean) {
    state.noiseCancellationCompatible = status;
  },
  updateChannelPeerConnectionState(
    state: MediaRoomState,
    payload: {
      channelName: string;
      updates: Partial<{
        peerConnectionState: RTCPeerConnectionState;
        peerConnectionStartTime: number;
        progressiveCloudProxyMode: number;
        isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus;
        currentProxyModeIndex: number;
        turnServerFallbackStatus: TurnServerFallbackStatus;
        turnServerUrl: string;
      }>;
    }
  ) {
    // Initialize channel state if it doesn't exist
    if (!state.channelPeerConnectionStates[payload.channelName]) {
      state.channelPeerConnectionStates[payload.channelName] = {
        peerConnectionState: 'new',
        status: 'DISCONNECTED',
        progressiveCloudProxyMode: undefined,
        isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus.NOT_STARTED,
        currentProxyModeIndex: 0,
        turnServerFallbackStatus: TurnServerFallbackStatus.NOT_STARTED,
        turnServerUrl: undefined
      };
    }

    Object.assign(state.channelPeerConnectionStates[payload.channelName], payload.updates);
  },
  setHDRecordingStatus(
    state: MediaRoomState,
    payload: {
      feed: 'user' | 'screenshare';
      status: HDRecordingStatus;
    }
  ) {
    state.hdRecordingStatus[payload.feed] = payload.status;
  },
  setSDRecordingStatus(
    state: MediaRoomState,
    payload: {
      feed: 'user' | 'screenshare';
      status: SDRecordingStatus;
    }
  ) {
    state.sdRecordingStatus[payload.feed] = payload.status;
  }
};

export type MediaRoomMutations = typeof mutations;
export default mutations;
